/**
 * Utility functions for date formatting across the portal.
 */

/**
 * Formats a Date object into Indian Standard date string (e.g., "10 Aug 2026").
 */
export const formatDateIN = (date?: Date | string | number | null): string => {
  if (!date) return "";
  const d = new Date(date);
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/**
 * Formats a Date object into Long US date string (e.g., "Monday, August 10, 2026").
 */
export const formatDateLong = (
  date?: Date | string | number | null,
): string => {
  if (!date) return "";
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

/**
 * Parses a "YYYY-MM-DD" string into a Date object without timezone shift issues.
 */
export const parseYMD = (dateStr: string): Date => {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d);
};

/** Formats a "YYYY-MM-DD" string as "DD/MM/YYYY". */
export const formatDateDMY = (dateStr: string): string => {
  const [year, month, day] = dateStr.split("-");
  return `${day}/${month}/${year}`;
};

/**
 * Formats a Date object into a "YYYY-MM-DD" string.
 */
export const formatYMD = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/**
 * WhatsApp-style date/time formatter for chat messages and conversation lists.
 */
export const formatChatMessageTime = (
  dateInput?: Date | string | number | null,
  mode: "bubble" | "list" = "bubble",
): string => {
  if (!dateInput) return "";
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return String(dateInput);

  const now = new Date();
  const isToday =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    d.getDate() === yesterday.getDate() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getFullYear() === yesterday.getFullYear();

  const timeStr = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  if (mode === "list") {
    if (isToday) return timeStr;
    if (isYesterday) return "Yesterday";

    const diffDays = Math.floor(
      (now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24),
    );
    if (diffDays < 7) {
      return d.toLocaleDateString("en-US", { weekday: "short" });
    }

    return d.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
    });
  }

  if (isToday) return timeStr;
  if (isYesterday) return `Yesterday ${timeStr}`;

  return `${d.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}, ${timeStr}`;
};
